import './Pagination.scss'
import { useId } from 'react'
import { MdOutlineChevronLeft, MdOutlineChevronRight } from 'react-icons/md'
import { clx } from '../clx'
import { IconButton } from '../IconButton'
import { PillSelect } from '../PillSelect'

/** react-admin's default `rowsPerPageOptions`, which the portal uses. */
export const DEFAULT_PER_PAGE_OPTIONS = [5, 10, 25, 50] as const

export interface PaginationProps {
  /** Current page, 1-based. */
  page: number
  perPage: number
  /** Total row count (X-Total-Count). `undefined` when the server did not say. */
  total?: number | undefined
  /** Whether a next page exists. Only used when `total` is unknown. */
  hasNextPage?: boolean
  /** Rows on the current page, to end the range precisely when `total` is unknown. */
  rowCount?: number
  perPageOptions?: readonly number[]
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
  /** Accessible name of the nav landmark. Default "Pagination". */
  label?: string
  className?: string
}

/** "1–25 of 300", or "26–50" when the total is unknown. */
export function formatRange(
  page: number,
  perPage: number,
  total?: number,
  rowCount?: number
): string {
  if (total === 0) return '0 of 0'
  if (total === undefined && rowCount === 0) return 'No rows'
  const start = (page - 1) * perPage + 1
  let end = page * perPage
  if (total !== undefined) end = Math.min(end, total)
  else if (rowCount !== undefined) end = start + rowCount - 1
  return total === undefined ? `${start}–${end}` : `${start}–${end} of ${total}`
}

export type PageItem = number | 'start-ellipsis' | 'end-ellipsis'

/**
 * The page buttons react-admin shows (MUI `usePagination`, boundaryCount 1,
 * siblingCount 1): first, last, the current page and one either side, with
 * an ellipsis for each gap; always seven slots once there are enough pages.
 */
export function pageItems(page: number, count: number): PageItem[] {
  const range = (from: number, to: number) =>
    Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => from + i)
  if (count <= 7) return range(1, count)
  const boundary = 1
  const sibling = 1
  const siblingsStart = Math.max(
    Math.min(page - sibling, count - boundary - sibling * 2 - 1),
    boundary + 2
  )
  const siblingsEnd = Math.min(Math.max(page + sibling, boundary + sibling * 2 + 2), count - 2)
  return [
    1,
    siblingsStart > boundary + 2 ? 'start-ellipsis' : boundary + 1,
    ...range(siblingsStart, siblingsEnd),
    siblingsEnd < count - boundary - 1 ? 'end-ellipsis' : count - boundary,
    count
  ]
}

export function Pagination({
  page,
  perPage,
  total,
  hasNextPage = false,
  rowCount,
  perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
  onPageChange,
  onPerPageChange,
  label = 'Pagination',
  className
}: PaginationProps) {
  const selectId = `pagination-${useId()}`
  const hasNext = total === undefined ? hasNextPage : page * perPage < total
  const hasPrevious = page > 1
  const options = perPageOptions.includes(perPage)
    ? perPageOptions
    : [...perPageOptions, perPage].sort((a, b) => a - b)
  // Page numbers need the total; without it only previous/next can work.
  const pageCount = total === undefined ? undefined : Math.max(1, Math.ceil(total / perPage))

  return (
    <nav aria-label={label} className={clx('pagination', className)}>
      <div className="pagination__per-page">
        <label htmlFor={selectId} className="pagination__per-page-label">
          Rows per page
        </label>
        <PillSelect
          id={selectId}
          className="pagination__select"
          value={String(perPage)}
          options={options.map((option) => ({ value: String(option), label: String(option) }))}
          onChange={(next) => {
            onPerPageChange(Number(next))
          }}
        />
      </div>
      <p className="pagination__range" aria-live="polite">
        {formatRange(page, perPage, total, rowCount)}
      </p>
      <div className="pagination__buttons">
        <IconButton
          label="Previous page"
          icon={<MdOutlineChevronLeft />}
          disabled={!hasPrevious}
          onClick={() => {
            onPageChange(page - 1)
          }}
        />
        {pageCount !== undefined && pageCount > 1 && (
          <ol className="pagination__pages">
            {pageItems(page, pageCount).map((item) =>
              typeof item === 'number' ? (
                <li key={item}>
                  <button
                    type="button"
                    className="pagination__page"
                    aria-label={`Go to page ${item}`}
                    aria-current={item === page ? 'page' : undefined}
                    onClick={() => {
                      if (item !== page) onPageChange(item)
                    }}
                  >
                    {item}
                  </button>
                </li>
              ) : (
                <li key={item} className="pagination__ellipsis" aria-hidden="true">
                  …
                </li>
              )
            )}
          </ol>
        )}
        <IconButton
          label="Next page"
          icon={<MdOutlineChevronRight />}
          disabled={!hasNext}
          onClick={() => {
            onPageChange(page + 1)
          }}
        />
      </div>
    </nav>
  )
}
